import { useRef, useEffect, useState } from 'react'
import { X, Calendar, Clock, Users, Film } from 'lucide-react'
import { useModal } from '@/context/ModalContext'
import TicketService from '@/services/api/ticketService'

const MovieDetailModal = ({ movie, onClose }) => {
  const rows = {
    A: Array.from({ length: 17 }, (_, i) => i + 1),
    B: Array.from({ length: 18 }, (_, i) => i + 1),
    C: Array.from({ length: 17 }, (_, i) => i + 1),
    D: Array.from({ length: 18 }, (_, i) => i + 1),
    E: Array.from({ length: 17 }, (_, i) => i + 1),
    F: Array.from({ length: 17 }, (_, i) => i + 1),
    G: Array.from({ length: 17 }, (_, i) => i + 1),
    H: Array.from({ length: 18 }, (_, i) => i + 1),
    I: Array.from({ length: 17 }, (_, i) => i + 1),
    J: Array.from({ length: 18 }, (_, i) => i + 1),
    K: Array.from({ length: 17 }, (_, i) => i + 1),
    L: Array.from({ length: 12 }, (_, i) => i + 1),
    M: Array.from({ length: 13 }, (_, i) => i + 1),
  }

  const maxSeats = Math.max(...Object.values(rows).map((r) => r.length))

  const [koltuklar, setKoltuklar] = useState([])
  const [filledSeats, setFilledSeats] = useState([])

  const modal = useModal()

  const toggleSeat = (seat) => {
    if (filledSeats.includes(seat)) {
      modal.show('Bu koltuk zaten dolu, lütfen başka bir koltuk seçiniz.')
      return
    }

    if (koltuklar.length > 1 && !koltuklar.includes(seat)) {
      modal.show('En fazla 2 adet bilet seçebilirsiniz.')
      return
    }

    setKoltuklar((prev) => (prev.includes(seat) ? prev.filter((s) => s !== seat) : [...prev, seat]))
  }

  const handleBuyTicket = async (bilet) => {
    if (koltuklar.length <= 0) {
      modal.show('Devam etmek için en az bir bilet seçmelisiniz.')
      return
    }

    const form = { ...bilet, ...{ koltuklar: koltuklar } }
    const result = await TicketService.save(form)

    if (result?.error) {
      modal.show(result.error)
      return
    }

    modal.show(result.message)

    onClose()

    setTimeout(() => {
      window.location.reload()
    }, 1500)
  }

  const contentRef = useRef(null)

  useEffect(() => {
    if (movie?.takenSeats) {
      setFilledSeats(movie.takenSeats) // ['A1','A2','B15', ...]
    }
  }, [movie])

  useEffect(() => {
    if (contentRef.current) {
      const el = contentRef.current

      // En başa koy
      el.scrollTo({ left: 0 })

      // 300ms sonra sağa doğru kaydır (scroll olduğunu belli eder)
      setTimeout(() => {
        el.scrollTo({ left: 80, behavior: 'smooth' })
      }, 300)

      // sonra tekrar sola getir → bounce efekti
      setTimeout(() => {
        el.scrollTo({ left: 0, behavior: 'smooth' })
      }, 900)
    }
  }, [])

  return (
    <div className='fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4'>
      <div className='bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto' onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className='sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10'>
          <div>
            <h2 className='text-xl text-start font-bold text-gray-800'>Koltuk Seç</h2>
            <span className='text-gray-500'>En fazla 2 adet bilet seçebilirsiniz.</span>
          </div>
          <button onClick={onClose} className='p-2 hover:bg-gray-100 rounded-full transition-colors'>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className='p-3'>
          {/* Info Grid */}
          <div className='grid grid-cols-2 gap-4 mb-3'>
            {movie.date && (
              <div className='bg-gray-50 p-2 py-3 relative rounded-lg border border-[#AC5E2A]'>
                <Calendar size={20} className='text-[#AC5E2A] absolute top-2 left-2' />
                <p className='text-xs text-gray-600'>Tarih</p>
                <p className='font-semibold text-sm'>{movie.date}</p>
              </div>
            )}

            {movie.hour && (
              <div className='bg-gray-50 p-2 py-3 relative rounded-lg border border-[#AC5E2A]'>
                <Clock size={20} className='text-[#AC5E2A] absolute top-2 left-2' />
                <p className='text-xs text-gray-600'>Saat</p>
                <p className='font-semibold text-sm'>{movie.hour}</p>
              </div>
            )}
          </div>

          <div className='overflow-x-auto mb-4' ref={contentRef}>
            <div className='flex gap-1 w-max'>
              {Array.from({ length: maxSeats }).map((_, colIndex) => (
                <div key={colIndex} className='flex flex-col gap-1'>
                  {Object.entries(rows).map(([rowKey, seats]) => {
                    const seatNumber = seats[colIndex]

                    if (!seatNumber) return <div key={rowKey} className='w-12 h-7 opacity-0'></div> // boş yer

                    const seatId = `${rowKey}${seatNumber}`
                    const selected = koltuklar.includes(seatId)

                    const isFilled = filledSeats.includes(seatId)

                    return (
                      <div
                        key={seatId}
                        onClick={() => toggleSeat(seatId)}
                        className={`
                        w-12 h-7 flex items-center justify-center border rounded cursor-pointer 
                        ${isFilled ? 'bg-red-600 text-white cursor-not-allowed' : ''}
                        ${selected ? 'bg-green-700 text-white' : ''}
                      `}
                      >
                        {seatId}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Button */}
          <button onClick={() => handleBuyTicket(movie)} className='cursor-pointer block w-full bg-[#AC5E2A] text-white text-center py-3 rounded-xl font-semibold hover:bg-[#AC5E2A]/90 transition-colors'>
            Hemen Bilet Al
          </button>
        </div>
      </div>
    </div>
  )
}

export default MovieDetailModal
